import "@styles/sharedtheme.scss"

import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { configure as configureDXLicense } from "@services/devextreme/license-helper"
import Toast, { type PluginOptions } from "vue-toastification"
import { setupApi } from "@services/http/api-service"
import { ExtendedUserInfo, configureAuthService, useAuthentication } from "@services/auth"
import { configureI18n } from "@services/i18n/i18nservice"
import App from './App.vue'
import { configureRouter, useRouter } from "@services/routing-services"
import { configureAppRoutes } from "./router"
import { configureAuthorization } from "./.shared/services/auth/authorization"
import { autoApplyLayoutToRoutesWithAuth } from "./.shared/services/layout"
import PublicLayout from "./components/layouts/PublicLayout.vue"
import PrivateLayout from "./components/layouts/PrivateLayout.vue"
import NotFoundView from "./.shared/views/NotFoundView.vue"
import { rand } from "@vueuse/core"
import { useApi } from "./.shared/.api/integration"
import { loadMessages, locale } from "devextreme/localization"
import { initializeLinq } from "linq-to-typescript"

configureDXLicense();
initializeLinq();

if (import.meta.env.VITE_IS_DEBUG === "true") {
    (window as any)["instance_id"] = rand(0, 99999);
}

const app = createApp(App)

configureAuthService({
    oauthProxyBaseUrl: import.meta.env.VITE_API_BASE_URL,
    userInfoGetter: async () => {
        const response = await useApi().callStandardApi("usuarioGetCurrentUserProfile", {});
        const profile = response as typeof response & { ModulosPds?: string[] };
        return new ExtendedUserInfo(
            response.IdUsuario,
            response.Username,
            response.FirstName,
            response.LastName,
            response.Email,
            response.Permissions,
            response.TieneInformesVariable === true,
            response.TieneInformesFijo === true,
            Array.isArray(profile.ModulosPds) ? profile.ModulosPds : [],
            response.PuedeUsarIa === true,
            response.SaldoDisponibleIa ?? 0
        );
    },
    userInfoToSerializable: info => ExtendedUserInfo.toSerializable(info as ExtendedUserInfo),
    userInfoSerializedToUserInfo: data => ExtendedUserInfo.fromSerializable(data)
});

await configureI18n({
    app: app,
    defaultLocale: import.meta.env.VITE_DEFAULT_LOCALE,
    fallbackLocales: import.meta.env.VITE_FALLBACK_LOCALES === "" ? undefined : JSON.parse(import.meta.env.VITE_FALLBACK_LOCALES),
    supportedLocales: import.meta.env.VITE_SUPPORTED_LOCALES === "" ? undefined : JSON.parse(import.meta.env.VITE_SUPPORTED_LOCALES),
    localeSelectorFallbackMode: "LanguageIgnoreRegion",
    logWarns: import.meta.env.VITE_SUPPRESS_I18N_WARNINGS !== "true",
    onLocaleChanged: async (l) => {
        const lang = l.split("-")[0];
        try {
            const messages = await import(`@/../../node_modules/devextreme/localization/messages/${lang}.json`);
            loadMessages(messages.default);
            locale(l);
        }
        catch (e) {
            console.warn(`No se puedo cargar localización de DevExtreme para el idioma ${lang}`);
        }
    }
});

const auth = useAuthentication();
setupApi();


auth.onUserInfoChanged((_, info) => {
    if (info == null) {
        useRouter().replace({ name: "home" });
    }
});

configureRouter(router => { router.addRoute("not-found", b => b.isRoot().withPath("/:pathMatch(.*)*").allowAnonymous().withComponent(NotFoundView)) });
configureAuthorization({
    commonRoutePermissionChecker: (userInfo, to) => {

        const rootPermission = import.meta.env.VITE_ROOT_PERMISSION;
        if (rootPermission != null && userInfo.permissions.has(rootPermission)) {
            return true;
        }

        const globalRequiredPermission = import.meta.env.VITE_GLOBAL_REQUIRED_PERMISSION;
        if (globalRequiredPermission != null && !userInfo.permissions.has(globalRequiredPermission)) {
            return false;
        }

        return true;
    }
})

configureAppRoutes();
autoApplyLayoutToRoutesWithAuth(PublicLayout, PrivateLayout);

app.use(createPinia())
app.use(Toast, {} as PluginOptions);

const router = useRouter();
app.use(router)
app.mount('#app')
